import React, { useState, useEffect, useRef } from "react";
import Navber from "../Components/Navber.jsx";
import ResortSidebar from "../Components/ResortSidebar.jsx";
import ResortList from "../Components/ResortList.jsx";
import "../Styles/Hotel_page.css";
import api from "../Api.js";
import HotelDrawer from "../Components/HotelDrawer.jsx";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { Autocomplete, TextField, Button, CircularProgress, Box } from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Skeleton from "@mui/material/Skeleton";
import SearchIcon from "@mui/icons-material/Search";

const ResortCardSkeleton = () => {
  return (
    <div className="resortcard-container">
      <div className="resortcard-left">
        <Skeleton variant="rectangular" width="100%" height={180} animation="wave" />
        <div style={{ display: "flex", gap: "5px", marginTop: "5px" }}>
          <Skeleton variant="rectangular" width="50%" height={70} animation="wave" />
          <Skeleton variant="rectangular" width="50%" height={70} animation="wave" />
        </div>
      </div>
      <div className="resortcard-middle">
        <Skeleton width="60%" height={30} animation="wave" />
        <Skeleton width="30%" height={25} animation="wave" />
        <Skeleton width="80%" height={20} animation="wave" />
        <Skeleton width="90%" height={20} animation="wave" />
        <Skeleton width="85%" height={20} animation="wave" />
        <div style={{ display: "flex", gap: "20px", marginTop: "10px" }}>
          <div style={{ flex: 1 }}>
            <Skeleton width="90%" height={20} />
            <Skeleton width="80%" height={20} />
            <Skeleton width="70%" height={20} />
          </div>
          <div style={{ flex: 1 }}>
            <Skeleton width="85%" height={20} />
            <Skeleton width="75%" height={20} />
            <Skeleton width="65%" height={20} />
          </div>
        </div>
      </div>
      <div className="resortcard-right">
        <Skeleton width="100%" height={25} />
        <Skeleton width="70%" height={40} />
        <Skeleton width="60%" height={20} />
        <Skeleton variant="rectangular" width="100%" height={40} style={{ marginTop: "10px" }} />
        <Skeleton width="100%" height={25} style={{ marginTop: "10px" }} />
      </div>
    </div>
  );
};

function Hotel_page() {
  const [hotelData, setHotelData] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false); // Loading more items at the bottom
  const [destinations, setDestinations] = useState([]);
  const [destination, setDestination] = useState(null);
  const [dateRange, setDateRange] = useState({ checkIn: null, checkOut: null });
  const [filters, setFilters] = useState({
    priceRange: null,
    propertyTypes: [],
    houseRules: [],
    roomViews: [],
  });
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  
  // Pagination States
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 10; // Number of hotels to fetch per page

  const navigate = useNavigate();

  // Refs for Infinite Scrolling & Locking
  const observerTarget = useRef(null);
  const isFetchingRef = useRef(false); // Synchronous lock to prevent duplicate API calls
  const searchPayloadRef = useRef(null); // Stores the last search payload for pagination

  // ✅ Unified Fetch Function (Handles both Initial Load and Pagination)
  const fetchHotelsData = async (currentPage, payload = null, isInitial = false) => {
    if (isFetchingRef.current) return; // Block if already fetching
    isFetchingRef.current = true;

    if (isInitial) setIsLoading(true);
    else setIsFetchingMore(true);

    try {
      let res;
      if (payload) {
        // Search Mode (POST) with pagination
        res = await api.post(`/availability/all?page=${currentPage}&limit=${LIMIT}`, payload);
      } else {
        // Normal Mode (GET) with pagination
        res = await api.get(`/hotels?page=${currentPage}&limit=${LIMIT}`);
      }

      const newData = res.data.data || [];

      // If we received fewer items than the limit, we've reached the end
      if (newData.length < LIMIT) {
        setHasMore(false);
      }

      if (isInitial) {
        setHotelData(newData);
        setFilteredItems(newData);
      } else {
        // Append new data and prevent duplicates
        setHotelData((prev) => {
          const existingIds = new Set(prev.map((item) => item.id));
          const uniqueNewData = newData.filter((item) => !existingIds.has(item.id));
          return [...prev, ...uniqueNewData];
        });
        setFilteredItems((prev) => [...prev, ...newData]);
      }
    } catch (err) {
      console.error("Error fetching hotels:", err);
      if (isInitial) toast.error("Failed to load hotels.");
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
      setIsFetchingMore(false);
    }
  };

  // ✅ Initial Load (Page 1, No Search Payload)
  useEffect(() => {
    fetchHotelsData(1, null, true);
  }, []);

  // ✅ Load More when Page State Changes
  useEffect(() => {
    if (page > 1) {
      fetchHotelsData(page, searchPayloadRef.current, false);
    }
  }, [page]);

  // ✅ Intersection Observer for Infinite Scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // If sentinel is visible, we have more data, and we aren't fetching
        if (entries[0].isIntersecting && hasMore && !isFetchingRef.current && !isLoading) {
          setPage((prevPage) => prevPage + 1);
        }
      },
      { threshold: 0.1 } // Trigger when 10% of the sentinel is visible
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) observer.unobserve(observerTarget.current);
    };
  }, [hasMore, isLoading]);

  // ✅ Load Destinations (Unchanged)
  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const res = await api.get("/destinations", {
          headers: {
            'X-API-TOKEN': '50cd682741a5119f4fc86830fe99960aaad2fb302377586ef753599def7af8b5',
          },
        });
        if (res.data.status === true) {
          const mapped = res.data.data.flatMap((d) => {
            const stateOption = { id: d.id, type: "state", label: d.name };
            const cityOptions = d.cities.map((c) => ({
              id: c.id,
              destination_id: d.id,
              type: "city",
              label: `${c.name}, ${d.name}`,
              city: c.name,
              state: d.name,
            }));
            return [stateOption, ...cityOptions];
          });
          setDestinations(mapped);
        }
      } catch (err) {
        console.log(err);
      }
    };
    fetchDestinations();
  }, []);

  const handleDateChange = (newValue, field) => {
    setDateRange((prev) => ({ ...prev, [field]: newValue }));
  };

  // ✅ Handle Search (Resets Pagination)
  const handleSearch = async () => {
    if (!destination || !destination.id) {
      toast.error("Please select destination");
      return;
    }

    const payload = {
      destination_id: destination.type === "state" ? destination.id : destination.destination_id,
      city_id: destination.type === "city" ? destination.id : null,
    };

    if (dateRange.checkIn) payload.check_in = dateRange.checkIn.toISOString().slice(0, 10);
    if (dateRange.checkOut) payload.check_out = dateRange.checkOut.toISOString().slice(0, 10);

    // Save payload for pagination
    searchPayloadRef.current = payload;
    
    // Reset states for new search
    setPage(1);
    setHasMore(true);
    setHotelData([]);
    setFilteredItems([]);
    
    // Fetch Page 1 of Search
    fetchHotelsData(1, payload, true);
  };

  return (
    <>
      <div style={{ backgroundColor: "#f0f0f0" }}>
        <Navber />
      </div>

      <div className="hotel-mobile-toolbar">
        <button
          type="button"
          className={`hotel-mobile-toolbar-button ${mobileSearchOpen ? "is-active" : ""}`}
          onClick={() => setMobileSearchOpen((isOpen) => !isOpen)}
          aria-expanded={mobileSearchOpen}
          aria-controls="hotel-search-panel"
        >
          <SearchIcon fontSize="small" />
          <span>Search</span>
        </button>
        <div className="hotel-mobile-filter-button">
          <HotelDrawer filters={filters} setFilters={setFilters} />
        </div>
      </div>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box
          id="hotel-search-panel"
          className={`hotel-searchbar-container hotel-animate-slideIn ${mobileSearchOpen ? "mobile-search-open" : ""}`}
          sx={{
            position: "sticky", top: 0, zIndex: 2, display: "flex", flexWrap: "wrap",
            gap: 2, justifyContent: "center", alignItems: "center",
            backgroundColor: "#fff", borderRadius: 2, boxShadow: 3, padding: 2, marginTop: 2,
          }}
        >
          <Autocomplete
            options={destinations}
            getOptionLabel={(option) => option.label || ""}
            value={destination}
            onChange={(event, newValue) => setDestination(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="State / City" variant="outlined" size="small"
                sx={{ minWidth: 220, "& .MuiInputBase-input": { padding: "10px 8px" } }}
              />
            )}
          />

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
            <DatePicker label="Check-in" value={dateRange.checkIn}
              onChange={(newValue) => handleDateChange(newValue, "checkIn")}
              minDate={new Date()} slotProps={{ textField: { size: "small", sx: { minWidth: 200 } } }}
            />
            <DatePicker label="Check-out" value={dateRange.checkOut}
              onChange={(newValue) => handleDateChange(newValue, "checkOut")}
              minDate={dateRange.checkIn || new Date()} slotProps={{ textField: { size: "small", sx: { minWidth: 200 } } }}
            />
          </Box>

          <Button variant="contained"
            sx={{
              backgroundColor: "#F17228", color: "#fff", fontWeight: 600,
              padding: "10px 25px", borderRadius: "8px", "&:hover": { backgroundColor: "#ff9100" },
            }}
            onClick={handleSearch}
          >
            SEARCH
          </Button>
        </Box>
      </LocalizationProvider>

      <div className="resort-app-container">
        <aside className="resort-sidebar">
          <ResortSidebar filters={filters} setFilters={setFilters} />
        </aside>

        <main className="resort-main-content">
          <div className="tourCardsWrapper">
            {isLoading ? (
              <div className="tourCardsGrid1">
                {Array.from(new Array(6)).map((_, i) => (
                  <ResortCardSkeleton key={i} />
                ))}
              </div>
            ) : (
              <>
                {filteredItems.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "50px", width: "100%" }}>
                    <h3>No hotels found matching your criteria.</h3>
                  </div>
                ) : (
                  <div className="tourCardsGrid1">
                    <ResortList filters={filters} hotelData={filteredItems} />
                  </div>
                )}

                {/* 🔄 Infinite Scroll Sentinel & Loading Skeleton */}
                {hasMore && (
                  <div ref={observerTarget} style={{ display: "flex", justifyContent: "center", margin: "20px 0" }}>
                    {isFetchingMore && (
                      <div style={{ width: "100%", maxWidth: "600px" }}>
                        <ResortCardSkeleton />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
}

export default Hotel_page;